/**
 * Axis-aligned bounding boxes, used for camera framing (F key) and the
 * selection bracket overlay.
 */
// Explicit extensions: also loaded by build tooling (the asset catalog's model bounds).
import { mat4TransformPoint } from "./mat4.ts";
import type { Mat4 } from "./mat4.ts";
import type { Vec3 } from "./vec3.ts";

export interface Aabb {
  readonly min: Vec3;
  readonly max: Vec3;
}

export function aabbFromPoints(points: readonly Vec3[]): Aabb {
  const min = [Infinity, Infinity, Infinity];
  const max = [-Infinity, -Infinity, -Infinity];

  for (const p of points) {
    for (let axis = 0; axis < 3; axis++) {
      const value = p[axis] ?? 0;
      min[axis] = Math.min(min[axis] ?? Infinity, value);
      max[axis] = Math.max(max[axis] ?? -Infinity, value);
    }
  }

  return { min: min as unknown as Vec3, max: max as unknown as Vec3 };
}

export function aabbUnion(a: Aabb, b: Aabb): Aabb {
  return aabbFromPoints([a.min, a.max, b.min, b.max]);
}

export function aabbCorners(box: Aabb): Vec3[] {
  const { min, max } = box;
  const corners: Vec3[] = [];
  for (const x of [min[0], max[0]]) {
    for (const y of [min[1], max[1]]) {
      for (const z of [min[2], max[2]]) corners.push([x, y, z]);
    }
  }
  return corners;
}

/** The world-space AABB enclosing a local box after transformation (conservative under rotation). */
export function aabbTransform(box: Aabb, m: Mat4): Aabb {
  return aabbFromPoints(aabbCorners(box).map((corner) => mat4TransformPoint(m, corner)));
}

export function aabbCenter(box: Aabb): Vec3 {
  return [
    (box.min[0] + box.max[0]) / 2,
    (box.min[1] + box.max[1]) / 2,
    (box.min[2] + box.max[2]) / 2,
  ];
}

export function aabbSize(box: Aabb): Vec3 {
  return [box.max[0] - box.min[0], box.max[1] - box.min[1], box.max[2] - box.min[2]];
}

/** True for the empty box produced by aabbFromPoints([]). */
export function aabbIsEmpty(box: Aabb): boolean {
  return box.min[0] > box.max[0];
}
